import React from 'react'
import clsx from 'clsx'
import { ChevronRightIcon } from '@heroicons/react/24/outline'
import type { UsageFunnel, UsageFunnelEvent } from '@shared/common-types.js'
import { changeRatio } from './format.js'

const numberFormat = new Intl.NumberFormat('zh-CN')

type Step = { event: UsageFunnelEvent; label: string }

/** 两行漏斗。转化率按人数算：同一个人点三次购买不该让转化率翻三倍 */
const SIGN_UP_STEPS: Step[] = [
  { event: 'sign_up', label: '新装' },
  { event: 'first_location', label: '首个定位' },
]
const PURCHASE_STEPS: Step[] = [
  { event: 'paywall_view', label: '看到付费墙' },
  { event: 'purchase_started', label: '开始购买' },
  { event: 'purchase_completed', label: '购买成功' },
]
/** 付费的两个出口，不在主线上，单独列 */
const PURCHASE_EXITS: Step[] = [
  { event: 'purchase_failure', label: '失败' },
  { event: 'purchase_cancelled', label: '取消' },
]

/** 「比昨天少 17%」：涨跌按大字（次数）算，写成一句话，不和下面的人数挨在一起被误读成人数的涨跌 */
function Change({ current, previous, previousName }: { current: number; previous: number; previousName: string }) {
  const ratio = changeRatio(current, previous)
  // 对比时段是 0 时比例没意义，直接写对比时段的数
  if (ratio === null) return <span className="text-gray-400">{previous === 0 ? `${previousName} 0` : '—'}</span>
  const percent = Math.round(Math.abs(ratio) * 100)
  if (percent === 0) return <span className="text-gray-400">和{previousName}持平</span>
  return (
    <span className={ratio > 0 ? 'text-green-600' : 'text-red-500'}>
      比{previousName}
      {ratio > 0 ? '多' : '少'} {percent}%
    </span>
  )
}

function StepTile({
  step,
  funnel,
  compareLabel,
  previousName,
  tone = 'normal',
}: {
  step: Step
  funnel: UsageFunnel
  compareLabel: string
  previousName: string
  tone?: 'normal' | 'bad'
}) {
  const { current, previous } = funnel[step.event]
  return (
    <div
      className="flex min-w-0 flex-col"
      title={`${step.event}：${numberFormat.format(current.events)} 次 / ${numberFormat.format(current.people)} 人。${compareLabel} ${numberFormat.format(previous.events)} 次`}
    >
      <span className="truncate text-xs text-gray-500 dark:text-gray-400">{step.label}</span>
      <span
        className={clsx(
          'text-xl font-bold tabular-nums sm:text-2xl',
          tone === 'bad' && current.events > 0 ? 'text-red-500' : 'dark:text-white',
        )}
      >
        {numberFormat.format(current.events)}
      </span>
      {current.people !== current.events && (
        <span className="whitespace-nowrap text-[11px] text-gray-400 sm:text-xs">
          {numberFormat.format(current.people)} 人
        </span>
      )}
      <span className="whitespace-nowrap text-[11px] sm:text-xs">
        <Change current={current.events} previous={previous.events} previousName={previousName} />
      </span>
    </div>
  )
}

/** 两步之间的箭头，下面是按人数算的转化率，标上「转化」免得和涨跌混在一起 */
function Arrow({ from, to, funnel }: { from: UsageFunnelEvent; to: UsageFunnelEvent; funnel: UsageFunnel }) {
  const base = funnel[from].current.people
  const reached = funnel[to].current.people
  const rate = base > 0 ? Math.round((reached / base) * 100) : null
  return (
    <div
      className="flex shrink-0 flex-col items-center px-0.5 pt-5 text-gray-400 sm:px-2"
      title={rate === null ? undefined : `按人数：${numberFormat.format(reached)} / ${numberFormat.format(base)} 人`}
    >
      <ChevronRightIcon className="h-4 w-4" />
      <span className="text-[11px] tabular-nums">{rate === null ? '—' : `${rate}%`}</span>
      <span className="text-[10px]">转化</span>
    </div>
  )
}

function Row({
  title,
  steps,
  funnel,
  compareLabel,
  previousName,
  children,
}: {
  title: string
  steps: Step[]
  funnel: UsageFunnel
  compareLabel: string
  previousName: string
  children?: React.ReactNode
}) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-sm font-semibold dark:text-white">{title}</span>
      <div className="flex flex-wrap items-start gap-y-2">
        {steps.map((step, i) => (
          <React.Fragment key={step.event}>
            {i > 0 && <Arrow from={steps[i - 1]!.event} to={step.event} funnel={funnel} />}
            <StepTile step={step} funnel={funnel} compareLabel={compareLabel} previousName={previousName} />
          </React.Fragment>
        ))}
        {children}
      </div>
    </div>
  )
}

/**
 * 注册与付费：今天多少人新装、拿到首个定位，多少人开始买、买成功、失败、取消。
 * 大字是次数（一个人可能连点好几次购买），人数不一样时写在下面；箭头下面是按人数算的转化率。
 */
export default function FunnelCard({
  funnel,
  compareLabel,
  previousName,
}: {
  funnel: UsageFunnel
  /** 「较昨天同时段」这类，用在说明和悬停提示里 */
  compareLabel: string
  /** 「昨天」「前 7 天」这类，对比时段是 0 时直接写「昨天 0」 */
  previousName: string
}) {
  return (
    <div className="grid grid-cols-1 gap-4 rounded-xl bg-white p-3 dark:bg-slate-700 md:grid-cols-[auto_1fr] md:gap-10 md:p-4">
      <Row title="注册" steps={SIGN_UP_STEPS} funnel={funnel} compareLabel={compareLabel} previousName={previousName} />
      <Row title="付费" steps={PURCHASE_STEPS} funnel={funnel} compareLabel={compareLabel} previousName={previousName}>
        <div className="ml-4 flex gap-4 border-l border-gray-200 pl-4 dark:border-slate-600 sm:ml-6 sm:gap-6 sm:pl-6">
          {PURCHASE_EXITS.map((step) => (
            <StepTile
              key={step.event}
              step={step}
              funnel={funnel}
              compareLabel={compareLabel}
              previousName={previousName}
              tone="bad"
            />
          ))}
        </div>
      </Row>
      <p className="text-[11px] text-gray-400 md:col-span-2">
        {`大字是次数，人数不同时写在下面。「比${previousName}多 / 少」按次数算（${compareLabel}）；箭头下的「转化」按人数算。只受国家 / 版本过滤，不受事件过滤。`}
      </p>
    </div>
  )
}
